import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dumbbell, Users, Clock, HelpCircle, CalendarPlus, Plus,
  Send, Loader2, MoreHorizontal, Upload, Shuffle, GitBranch, FileText, Trash2, UserMinus, Tags,
} from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '../ui/dropdown-menu';

export interface HeatsToolbarCounts {
  wods: number;
  categories: number;
  /** Couloirs par heat, lu sur le planning existant. */
  lanes?: number;
  heats: number;
  assignedTeams: number;
  publishable: number;
  futureDates: number;
}

export interface HeatsToolbarFlags {
  isCourseEvent: boolean;
  loading: boolean;
  publishLoading: boolean;
  /** Halo sur « Publier » pendant la visite guidée. */
  highlightPublish: boolean;
  /** La visite guidée neutralise le bouton pour ne rien envoyer pour de vrai. */
  tourForcePublish: boolean;
}

export interface HeatsToolbarActions {
  onGenerate: () => void;
  onCreate: () => void;
  onPublish: () => void;
  onAutoAssign: () => void;
  onUnassignAll: () => void;
  onImport: () => void;
  onReseed: () => void;
  onBulkStatus: () => void;
  onRedistribute: () => void;
  onExportPdf: () => void;
  onDelete: () => void;
  onHelp: () => void;
}

interface Props {
  counts: HeatsToolbarCounts;
  flags: HeatsToolbarFlags;
  publishButtonRef?: React.RefObject<HTMLButtonElement | null>;
  actions: HeatsToolbarActions;
}

/**
 * Barre d'outils du planning.
 *
 * Elle est décrite par quatre objets — ce qu'on compte, dans quel état on est,
 * comment on regarde, ce qu'on peut déclencher — et non par la vingtaine de
 * `setXxxDialogOpen` qu'elle pilotait en ligne. Ces `set…` sont l'état interne
 * de la page ; les exposer ici aurait fait de la barre le co-propriétaire des
 * douze fenêtres, exactement le couplage qui l'avait rendue inextricable.
 *
 * Hiérarchie (revue UX 2026-08-03) : Publier en primaire, seule action à
 * conséquence — elle envoie des emails aux participants ; Générer et Créer en
 * secondaire ; l'occasionnel dans un menu « ⋯ », Supprimer isolé en rouge.
 *
 * Sur un planning VIDE, la barre ne montre aucune action : il n'y a rien à
 * publier, réorganiser ni exporter, et créer se fait depuis l'état vide, au
 * centre de l'écran, là où le regard se pose.
 */
const HeatsToolbar: React.FC<Props> = ({
  counts, flags, publishButtonRef, actions,
}) => {
  const { t } = useTranslation();
  const hasHeats = counts.heats > 0;

  return (
    <div className="flex items-center justify-between gap-4 pb-4 mb-4 border-b border-border flex-wrap">
      <div className="flex gap-2 items-center flex-wrap">
        <Badge variant="outline" className="flex items-center gap-1">
          <Dumbbell size={14} /> {counts.wods} WODs
        </Badge>
        <Badge variant="outline" className="flex items-center gap-1">
          <Users size={14} /> {counts.categories} {t('categories.title')}
        </Badge>
        {hasHeats && !!counts.lanes && (
          <Badge variant="outline" className="flex items-center gap-1">
            <Clock size={14} /> {counts.lanes} {t('heats.lanes')}
          </Badge>
        )}

      </div>

      {hasHeats && (
        <div className="flex gap-2 items-center flex-wrap">
          <Button
            variant="ghost"
            size="sm"
            data-tour="heats-help"
            onClick={actions.onHelp}
            title={t('tour.helpButtonTitle')}
            aria-label={t('tour.helpButtonTitle')}
            className="text-muted-foreground hover:text-foreground"
          >
            <HelpCircle size={16} />
          </Button>

          <Button variant="secondary" size="sm" onClick={actions.onGenerate}>
            <CalendarPlus size={18} />
            <span className="ml-2 whitespace-nowrap">{t('heats.actions.generate')}</span>
          </Button>

          <Button variant="secondary" size="sm" onClick={actions.onCreate}>
            <Plus size={18} />
            <span className="ml-2 whitespace-nowrap">{t('heats.actions.create')}</span>
          </Button>

          {(counts.publishable > 0 || flags.tourForcePublish) && (
            // Halo d'appel sur un calque DERRIÈRE le bouton (le bouton est
            // `overflow-hidden`, un halo intérieur serait rogné) : une opacité
            // animée, 3 cycles puis repos à 35 % (le signal reste visible, seule la
            // pulsation s'arrête) — pas un box-shadow interpolé en boucle.
            <span className="relative isolate inline-flex">
              {flags.highlightPublish && (
                <span
                  aria-hidden
                  className="pointer-events-none absolute inset-0 -z-10 rounded-md opacity-35 shadow-[0_0_20px_6px_hsl(var(--primary)/0.6)] animate-halo motion-reduce:animate-none"
                />
              )}
              <Button
                ref={publishButtonRef}
                size="sm"
                data-tour="heats-publish-btn"
                onClick={() => { if (!flags.tourForcePublish) actions.onPublish(); }}
                disabled={flags.publishLoading || flags.tourForcePublish}
                title={t('heats.publishTitle')}
              >
                {flags.publishLoading
                  ? <Loader2 size={18} className="animate-spin" />
                  : <Send size={18} />}
                <span className="ml-1 hidden sm:inline">{t('heats.actions.publish')}</span>
              </Button>
            </span>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline" size="sm" className="px-2"
                title={t('heats.actions.more')} aria-label={t('heats.actions.more')}
              >
                <MoreHorizontal size={18} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {/* Assignation en masse : héritée de l'écran Assignations, qui
                  n'existe plus en tant que page. */}
              <DropdownMenuItem data-tour="assign-auto" onClick={actions.onAutoAssign}>
                <Users size={15} className="mr-2 text-muted-foreground" />
                {t('assignments.autoAssign')}
              </DropdownMenuItem>
              {counts.assignedTeams > 0 && (
                <DropdownMenuItem onClick={actions.onUnassignAll}>
                  <UserMinus size={15} className="mr-2 text-muted-foreground" />
                  {t('assignments.unassignAll')}
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={actions.onImport}>
                <Upload size={15} className="mr-2 text-muted-foreground" />
                {t('heats.actions.import')}
              </DropdownMenuItem>
              {counts.futureDates > 0 && (
                <DropdownMenuItem onClick={actions.onReseed}>
                  <Shuffle size={15} className="mr-2 text-muted-foreground" />
                  {t('heats.actions.reorganize')}
                </DropdownMenuItem>
              )}
              {flags.isCourseEvent && (
                <DropdownMenuItem onClick={actions.onRedistribute}>
                  <GitBranch size={15} className="mr-2 text-muted-foreground" />
                  {t('heats.actions.redistribute')}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={actions.onBulkStatus}>
                <Tags size={15} className="mr-2 text-muted-foreground" />
                {t('heats.bulkStatus.menu')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={actions.onExportPdf} disabled={flags.loading}>
                <FileText size={15} className="mr-2 text-muted-foreground" />
                {t('heats.actions.pdf')}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={actions.onDelete}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 size={15} className="mr-2" />
                {t('heats.deleteHeats.menu')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </div>
  );
};

export default HeatsToolbar;
